import { useCallback, useRef, useState } from 'react';

/**
 * Two-step export: open scope dialog, then run pending action with selected scope.
 *
 * @returns {{
 *   open: boolean,
 *   pendingFormat: 'csv' | 'excel' | null,
 *   scopeCounts: { currentPage: number, filtered: number, all: number },
 *   openScopeDialog: (format: 'csv' | 'excel', runExport: (scope: string) => void | Promise<void>, counts: object) => void,
 *   confirm: (scope: string) => void,
 *   cancel: () => void,
 * }}
 */
export function useExportScope() {
  const pendingActionRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pendingFormat, setPendingFormat] = useState(null);
  const [scopeCounts, setScopeCounts] = useState({ currentPage: 0, filtered: 0, all: 0 });

  const openScopeDialog = useCallback((format, runExport, counts) => {
    pendingActionRef.current = runExport;
    setPendingFormat(format);
    setScopeCounts({
      currentPage: counts?.currentPage ?? 0,
      filtered: counts?.filtered ?? 0,
      all: counts?.all ?? 0,
    });
    setOpen(true);
  }, []);

  const confirm = useCallback((scope) => {
    const fn = pendingActionRef.current;
    pendingActionRef.current = null;
    setOpen(false);
    setPendingFormat(null);
    void fn?.(scope);
  }, []);

  const cancel = useCallback(() => {
    pendingActionRef.current = null;
    setOpen(false);
    setPendingFormat(null);
  }, []);

  return { open, pendingFormat, scopeCounts, openScopeDialog, confirm, cancel };
}
